import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { appInsights } from '../../../../telemetry/appInsights';
import { useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { LanguageContext } from '../../../../context/languageContext';
import { useAuth, userHasGroup } from '../../../../context/authContext';
import { useRetailerSkillingSheet } from './useRetailerSkillingSheet';
import { useCsSkillingSheet } from './useCsSkillingSheet';
import { canAccessJurisdiction } from './jurisdictionSkills';
import {
  getRetailerRepsFromRepInfoResponse,
  mapRecentConsumablesFromApi,
  mapRecentFormsFromApi,
  removeDuplicateContacts,
  repListToContacts,
  sortContactsByLastName,
} from './utils';
import { getSiteValue } from '../../../../data/contactCenter/aurora/dropDownOptions';

async function getPasswordVerification({ retailerOid, site }) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT_HOST}/passwordVerification`,
      {
        params: {
          retailerId: retailerOid,
          site,
        },
      },
    );

    const value = response.data?.passwordVerification;
    const tellers = (Array.isArray(value)
      ? value
      : value && typeof value === 'object'
        ? Object.values(value)
        : []
    )
      .filter(teller => teller?.password != null && teller.password !== '')
      .sort((a, b) => Number(a.password) - Number(b.password));

    return { response, tellers };
  } catch (error) {
    console.error('passwordVerification request/parse failed:', error);
    return { response: null, tellers: [] };
  }
}

export function useAuroraRetailerSearch() {
  const { accessiblePaths, userGroups, user } = useAuth();
  const isAuroraAdmin = userHasGroup(userGroups, 'Engage_Administrators');
  const { translations } = useContext(LanguageContext);
  const { skills: retailerSkills, loading: retailerSkillsLoading } =
    useRetailerSkillingSheet();
  const { skills: csSkills, loading: csSkillsLoading } = useCsSkillingSheet();
  const skillsLoading = retailerSkillsLoading || csSkillsLoading;

  const [jurisdiction, setJurisdiction] = useState('');
  const [retailerNumber, setRetailerNumber] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [retailer, setRetailer] = useState(null);
  const [reps, setReps] = useState([]);
  const [repsLoading, setRepsLoading] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [terminals, setTerminals] = useState([]);
  const [terminalsLoading, setTerminalsLoading] = useState(false);
  const [passwordTellers, setPasswordTellers] = useState([]);
  const [passwordVerificationLoading, setPasswordVerificationLoading] =
    useState(false);
  const passwordVerificationResponseRef = useRef(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isInventoryReviewModalOpen, setIsInventoryReviewModalOpen] = useState(false);
  const [isRecentFormsModalOpen, setIsRecentFormsModalOpen] = useState(false);
  const [recentForms, setRecentForms] = useState([]);
  const [recentFormsLoading, setRecentFormsLoading] = useState(false);
  const [isRecentConsumablesModalOpen, setIsRecentConsumablesModalOpen] =
    useState(false);
  const [recentConsumables, setRecentConsumables] = useState([]);
  const [hostSearchParams, setHostSearchParams] = useState(null);
  const [isTerminalModalOpen, setIsTerminalModalOpen] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const trackRetailerSearch = useTrackEvent(appInsights, 'RetailerSearch');
  const trackRepInfoFetch = useTrackEvent(appInsights, 'RepInfoFetch');
  const trackTerminalFetch = useTrackEvent(appInsights, 'TerminalFetch');
  const trackViewPassword = useTrackEvent(appInsights, 'ViewPassword');

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };

  useEffect(() => {
    if (isAuroraAdmin || skillsLoading || !jurisdiction) {
      return;
    }
    if (!canAccessJurisdiction(jurisdiction, retailerSkills, csSkills)) {
      setJurisdiction('');
      setRetailerNumber('');
    }
  }, [isAuroraAdmin, skillsLoading, retailerSkills, csSkills, jurisdiction]);

  const loadReps = async hostContext => {
    setRepsLoading(true);
    setReps([]);

    try {
      const site = hostContext?.site;
      const repInfoRetailerId = String(hostContext?.retailerOid || '').trim();

      const retailerRepResponse = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_HOST}/repInfo`,
        {
          params: {
            site,
            retailerId: repInfoRetailerId,
          },
        },
      );
      const { repInfo } = retailerRepResponse.data;

      try {
        trackRepInfoFetch({
          user,
          searchParams: {
            site,
            repInfoRetailerId,
          },
          retailerRepResponse,
        });
      } catch (e) {
        console.warn('Telemetry trackRepInfoFetch failed:', e);
      }

      const retailerReps = getRetailerRepsFromRepInfoResponse(repInfo);
      const contacts = removeDuplicateContacts(repListToContacts(retailerReps));

      setReps(sortContactsByLastName(contacts));
    } catch (error) {
      console.warn('repInfo request failed:', error);
      setReps([]);

      let errorMessage =
        'Unable to load representative contacts for this retailer.';
      if (error.message) {
        errorMessage = error.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    } finally {
      setRepsLoading(false);
    }
  };

  const loadPasswordVerification = async hostContext => {
    const retailerOid = hostContext?.retailerOid;
    const site = hostContext?.site;

    if (!retailerOid || !site) {
      setPasswordTellers([]);
      passwordVerificationResponseRef.current = null;
      return;
    }

    setPasswordVerificationLoading(true);

    try {
      const { response, tellers } = await getPasswordVerification({
        retailerOid,
        site,
      });
      passwordVerificationResponseRef.current = response;
      setPasswordTellers(tellers);
    } catch (error) {
      console.warn('passwordVerification request failed:', error);
      setPasswordTellers([]);
      passwordVerificationResponseRef.current = null;
    } finally {
      setPasswordVerificationLoading(false);
    }
  };

  const loadTerminals = async hostContext => {
    const retailerOid = hostContext?.retailerOid;
    const site = hostContext?.site;

    setTerminalsLoading(true);

    try {
      const terminalInfoResponse = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_HOST}/terminalInfo`,
        {
          params: {
            retailerId: retailerOid,
            site,
          },
        },
      );
      const { terminalInfo } = terminalInfoResponse.data;

      const terminalTypesResponse = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_HOST}/terminalTypes`,
        {
          params: { site },
        },
      );
      const { terminalTypes } = terminalTypesResponse.data;

      try {
        trackTerminalFetch({
          user,
          searchParams: {
            site,
            retailerOid,
          },
          terminalInfoResponse,
          terminalTypesResponse,
        });
      } catch (e) {
        console.warn('Telemetry trackTerminalFetch failed:', e);
      }

      terminalInfo.forEach(terminal => {
        terminalTypes.forEach(type => {
          if (terminal.terminalType === type.terminalType) {
            terminal.description = type.description;
          }
        });
      });

      setTerminals(terminalInfo);
      return terminalInfo;
    } catch (error) {
      console.warn('Error fetching terminal info and terminal types:', error);
      setTerminals([]);
      return [];
    } finally {
      setTerminalsLoading(false);
    }
  };

  const loadRecentForms = async (jurisdictionName, retailerId) => {
    if (!jurisdictionName || !retailerId) {
      setRecentForms([]);
      setRecentConsumables([]);
      return [];
    }

    setRecentFormsLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_REPORTING}/uscc/aurora/retailerRecentForms`,
        {
          params: {
            jurisdiction: jurisdictionName,
            retailerId,
          },
        },
      );
  
      const mappedForms = mapRecentFormsFromApi(response.data);
      const mappedConsumables = mapRecentConsumablesFromApi(response.data);
      setRecentForms(mappedForms);
      setRecentConsumables(mappedConsumables);
      return mappedForms;
    } catch (error) {
      console.warn('recentForms request failed:', error);
      return [];
    } finally {
      setRecentFormsLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setRetailer(null);
    setReps([]);
    setTerminals([]);
    setPasswordTellers([]);
    setPasswordVerificationLoading(false);
    passwordVerificationResponseRef.current = null;
    setIsPasswordModalOpen(false);
    setIsInventoryReviewModalOpen(false);
    setRecentForms([]);
    setRecentFormsLoading(false);
    setRecentConsumables([]);
    setIsRecentConsumablesModalOpen(false);
    setHostSearchParams(null);
    const site = getSiteValue(jurisdiction);

    if (!site) {
      setSnackbarMessage(
        'This jurisdiction is not configured for Host API search.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setSearchLoading(false);
      return;
    }

    const searchParams = {
      retailerId: retailerNumber.trim(),
      site,
    };

    try {
      const retailerInfoResponse = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_HOST}/retailerInfo`,
        { params: searchParams },
      );

      const { retailerInfo } = retailerInfoResponse.data;
      const info = retailerInfo.length > 0 ? retailerInfo[0] : null;
      const mailingAddress =
        info?.addresses?.find(addr => addr.addressType === 1) ??
        info?.addresses?.[0];
      const retailerRecord = info
        ? {
            ...info,
            mailingAddress,
          }
        : null;

      if (!retailerRecord) {
        setSnackbarMessage(
          `No retailer was found for retailer number ${retailerNumber.trim()} in ${jurisdiction}. Verify the retailer number and selected jurisdiction, then try again.`,
        );
        setSnackbarSeverity('error');
        setIsSnackbarOpen(true);
        setSearchLoading(false);
        return;
      }

      const retailerOid = retailerRecord?.retailerOid;
      const hostContext = {
        retailerOid: retailerOid ? retailerOid : '',
        retailerNumber: retailerNumber.trim(),
        retailerId: retailerNumber.trim(),
        site,
        jurisdiction,
      };

      setRetailer(retailerRecord);
      setHostSearchParams(hostContext);
      loadTerminals(hostContext);
      loadReps(hostContext);
      loadPasswordVerification(hostContext);
      loadRecentForms(
        jurisdiction,
        hostContext.retailerNumber,
      );

      try {
        trackRetailerSearch({
          user,
          searchParams,
          retailerInfoResponse,
        });
      } catch (e) {
        console.warn('Telemetry trackRetailerSearch failed:', e);
      }

      setSnackbarMessage('Retailer Search Successful');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error fetching retailer info:', error, {
        jurisdiction,
        site,
        retailerId: retailerNumber.trim(),
      });
      setSnackbarMessage(
        'There was a problem searching for the retailer, please try again. If the problem persists contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }

    setSearchLoading(false);
  };

  const handleViewPasswordClick = () => {
    const response = passwordVerificationResponseRef.current;
    const retailerOid = hostSearchParams?.retailerOid;
    const site = hostSearchParams?.site;

    if (response && retailerOid && site) {
      try {
        trackViewPassword({
          user,
          searchParams: { retailerOid, site },
          passwordVerificationResponse: response,
        });
      } catch (e) {
        console.warn('Telemetry trackViewPassword failed:', e);
      }
    }

    setIsPasswordModalOpen(true);
  };

  const handleInventoryReviewModal = () => {
    setIsInventoryReviewModalOpen(true);
  };

  const handleOpenTerminalDetails = async terminal => {
    setSelectedTerminal(terminal);
    setIsTerminalModalOpen(true);
  };

  const handleCloseTerminalModal = () => {
    setIsTerminalModalOpen(false);
    setSelectedTerminal(null);
  };

  const handleOpenRecentFormsModal = () => {
    setIsRecentFormsModalOpen(true);
  };

  const handleCloseRecentFormsModal = () => {
    setIsRecentFormsModalOpen(false);
  };

  const handleOpenRecentConsumablesModal = () => {
    setIsRecentConsumablesModalOpen(true);
  };

  const handleCloseRecentConsumablesModal = () => {
    setIsRecentConsumablesModalOpen(false);
  };

  const clearSearchResults = () => {
    setRetailer(null);
    setReps([]);
    setRepsLoading(false);
    setTerminals([]);
    setTerminalsLoading(false);
    setPasswordTellers([]);
    setPasswordVerificationLoading(false);
    passwordVerificationResponseRef.current = null;
    setIsPasswordModalOpen(false);
    setIsInventoryReviewModalOpen(false);
    setIsRecentFormsModalOpen(false);
    setRecentForms([]);
    setRecentFormsLoading(false);
    setIsRecentConsumablesModalOpen(false);
    setRecentConsumables([]);
    setHostSearchParams(null);
    setRetailerNumber('');
    setSearchLoading(false);
    handleCloseTerminalModal();
  };

  return {
    accessiblePaths,
    isAuroraAdmin,
    translations,
    jurisdiction,
    setJurisdiction,
    retailerNumber,
    setRetailerNumber,
    searchLoading,
    retailer,
    reps,
    repsLoading,
    isSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    terminals,
    terminalsLoading,
    passwordTellers,
    passwordVerificationLoading,
    isPasswordModalOpen,
    isInventoryReviewModalOpen,
    isRecentFormsModalOpen,
    recentForms,
    recentFormsLoading,
    isRecentConsumablesModalOpen,
    recentConsumables,
    hostSearchParams,
    isTerminalModalOpen,
    selectedTerminal,
    handleCloseSnackbar,
    handleSearch,
    handleViewPasswordClick,
    handleInventoryReviewModal,
    handleOpenTerminalDetails,
    handleCloseTerminalModal,
    handleOpenRecentFormsModal,
    handleCloseRecentFormsModal,
    handleOpenRecentConsumablesModal,
    handleCloseRecentConsumablesModal,
    clearSearchResults,
    loadTerminals,
    setSnackbarMessage,
    setSnackbarSeverity,
    setIsSnackbarOpen,
    setSelectedTerminal,
    setIsPasswordModalOpen,
    setIsInventoryReviewModalOpen,
    user,
    retailerSkills,
    csSkills,
    skillsLoading,
  };
}
